import { useEffect, useRef, useState } from 'react';
import { motion, useScroll, useSpring } from 'framer-motion';
import './PrintingJourney.css';

const defaultTitles = ['Designing', 'Printing', 'Cleaning', 'Curing'];

function JourneyCard({ number, title, description, videoUrl, side }) {
  return (
    <motion.article
      className={`printing-journey-card printing-journey-card-${side}`}
      initial={{ opacity: 0, y: 32 }}
      whileInView={{ opacity: 1, y: 0 }}
      viewport={{ once: true, amount: 0.25 }}
      transition={{ duration: 0.55, ease: [0.25, 0.1, 0.25, 1] }}
    >
      <div className="printing-journey-card-media">
        <video src={videoUrl} muted loop autoPlay playsInline preload="metadata" aria-label={`${title} process`} />
      </div>
      <div className="printing-journey-card-body">
        <span className="printing-journey-step">0{number}</span>
        <h3>{title}</h3>
        <p>{description}</p>
      </div>
    </motion.article>
  );
}

function PrintingJourney({
  videoUrls,
  isArabic = false,
  stepTitles = defaultTitles,
  stepDescriptions = [
    'Shape every dental model with a clear, precise design workflow.',
    'Build precise dental models layer by layer.',
    'Clean and prepare every result for the next step.',
    'Cure each print for a durable, stable finish.',
  ],
}) {
  const sectionRef = useRef(null);
  const [reducedMotion, setReducedMotion] = useState(false);
  const { scrollYProgress } = useScroll({ target: sectionRef, offset: ['start 80%', 'end 20%'] });
  const lineProgress = useSpring(scrollYProgress, { stiffness: 90, damping: 28, restDelta: 0.001 });

  useEffect(() => {
    const mediaQuery = window.matchMedia('(prefers-reduced-motion: reduce)');
    const updateMotionPreference = () => setReducedMotion(mediaQuery.matches);
    updateMotionPreference();
    mediaQuery.addEventListener('change', updateMotionPreference);
    return () => mediaQuery.removeEventListener('change', updateMotionPreference);
  }, []);

  const titles = isArabic ? ['التصميم', 'الطباعة', 'التنظيف', 'المعالجة'] : [...defaultTitles, ...stepTitles].slice(0, 4);
  const descriptions = isArabic ? ['تصميم كل نموذج أسنان بدقة ووضوح.', 'إنشاء نماذج أسنان دقيقة طبقة بعد طبقة.', 'تنظيف وتجهيز كل نتيجة للخطوة التالية.', 'معالجة كل طبعة للحصول على نتيجة ثابتة.'] : [...stepDescriptions, '', '', ''].slice(0, 4);
  const urls = [...(videoUrls || []), '', '', ''].slice(0, 4);

  return (
    <section className={`printing-journey${isArabic ? ' printing-journey-arabic' : ''}`} id="printing-journey" ref={sectionRef} aria-labelledby="printing-journey-title">
      <div className="printing-journey-header">
        <p className="eyebrow">{isArabic ? 'المراحل' : 'THE PROCESS'}</p>
        <h2 id="printing-journey-title">{isArabic ? 'رحلة الطباعة' : 'Printing Journey'}</h2>
      </div>
      <div className="printing-journey-track">
        <svg className="printing-journey-line" viewBox="0 0 2 1000" preserveAspectRatio="none" aria-hidden="true">
          <path className="printing-journey-line-base" d="M1 0V1000" />
          <motion.path
            className="printing-journey-line-progress"
            d="M1 0V1000"
            initial={{ pathLength: 0 }}
            style={{ pathLength: reducedMotion ? 1 : lineProgress }}
          />
        </svg>
        <span className="printing-journey-node printing-journey-node-1" />
        <span className="printing-journey-node printing-journey-node-2" />
        <span className="printing-journey-node printing-journey-node-3" />
        <span className="printing-journey-node printing-journey-node-4" />
        <JourneyCard number={1} title={titles[0]} description={descriptions[0]} videoUrl={urls[0]} side="left" />
        <JourneyCard number={2} title={titles[1]} description={descriptions[1]} videoUrl={urls[1]} side="right" />
        <JourneyCard number={3} title={titles[2]} description={descriptions[2]} videoUrl={urls[2]} side="left" />
        <JourneyCard number={4} title={titles[3]} description={descriptions[3]} videoUrl={urls[3]} side="right" />
      </div>
    </section>
  );
}

export default PrintingJourney;
